import {
  forwardRef,
  lazy,
  Suspense,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";

/**
 * Lazy-load @marsidev/react-turnstile so challenges.cloudflare.com/api.js is NOT
 * injected until a visible form actually mounts this component (PopupManager on
 * `/` + `/products` must not pull CF until the email-capture dialog is open).
 */
const Turnstile = lazy(() =>
  import("@marsidev/react-turnstile").then((m) => ({ default: m.Turnstile }))
);

/** Cloudflare visible always-pass dummy sitekey (testing docs). */
export const CF_TEST_VISIBLE_PASS = "1x00000000000000000000AA";

const PROD_HOSTS = new Set(["pokecoins.cc", "www.pokecoins.cc"]);

/**
 * Preview / non-production hosts: use CF test sitekey.
 * Production sitekey is only registered for pokecoins.cc — anywhere else it
 * throws ParityDigital (`t().filter is not a function`) and/or shows
 * "This domain is not authorized for the provided site key".
 */
export function isTurnstileBypassHost(hostname) {
  if (!hostname) return true;
  const h = String(hostname).toLowerCase();
  if (PROD_HOSTS.has(h)) return false;
  if (h === "localhost" || h === "127.0.0.1" || h === "[::1]" || h === "::1") return true;
  if (h.endsWith(".ts.net")) return true;
  if (/^100\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(h)) return true;
  if (h.startsWith("192.168.") || h.startsWith("10.")) return true;
  if (h.endsWith(".local") || h.endsWith(".lan")) return true;
  if (h.includes("emergentagent.com") || h.includes("ngrok") || h.includes("trycloudflare")) {
    return true;
  }
  // Any host that is not pokecoins.cc → test key (prod key is domain-locked).
  return true;
}

function ancestorChainVisible(node) {
  if (!node || typeof window === "undefined") return false;
  let cur = node;
  while (cur && cur !== document.documentElement) {
    const st = window.getComputedStyle(cur);
    if (st.display === "none" || st.visibility === "hidden") return false;
    // Opacity 0 (shell-chrome--holding / transitions) → CF ParityDigital if we render now.
    if (Number(st.opacity) === 0) return false;
    cur = cur.parentElement;
  }
  const r = node.getBoundingClientRect();
  return r.width > 0;
}

/**
 * Defer the real widget until the host is painted + visible.
 * Also survives React StrictMode (dev) mount→unmount→remount without racing
 * two CF widgets onto the same container (ParityDigital).
 */
function useStableVisibleGate() {
  const hostRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let armTimer = null;
    const el = hostRef.current;

    const disarm = () => {
      if (armTimer != null) {
        window.clearTimeout(armTimer);
        armTimer = null;
      }
      if (!cancelled) setReady(false);
    };

    const arm = () => {
      if (cancelled || !el) return;
      if (document.visibilityState !== "visible") return;
      if (!ancestorChainVisible(el)) return;
      // Macrotask past StrictMode cleanup + CSS visibility transition edge.
      if (armTimer != null) window.clearTimeout(armTimer);
      armTimer = window.setTimeout(() => {
        if (cancelled || !el) return;
        if (document.visibilityState !== "visible") return;
        if (!ancestorChainVisible(el)) return;
        setReady(true);
      }, 120);
    };

    const io =
      typeof IntersectionObserver !== "undefined"
        ? new IntersectionObserver(
            (entries) => {
              if (entries.some((e) => e.isIntersecting && e.intersectionRatio > 0)) arm();
              else disarm();
            },
            { threshold: [0, 0.01, 0.1] }
          )
        : null;
    if (el && io) io.observe(el);

    arm();
    const poll = window.setInterval(arm, 150);
    const pollStop = window.setTimeout(() => window.clearInterval(poll), 3000);
    const onVis = () => (document.visibilityState === "visible" ? arm() : disarm());
    document.addEventListener("visibilitychange", onVis);

    return () => {
      cancelled = true;
      disarm();
      io?.disconnect();
      window.clearInterval(poll);
      window.clearTimeout(pollStop);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  return { hostRef, ready };
}

/**
 * Thin Turnstile wrapper.
 * - Lazy @marsidev import → CF script only after this mounts AND host is visible.
 * - Unique container id per instance (marsidev defaults every widget to
 *   `id="cf-turnstile"`; colliding ids → ParityDigital).
 * - Preview hosts: Cloudflare dummy visible always-pass sitekey.
 * - Real hosts (pokecoins.cc): REACT_APP_TURNSTILE_SITE_KEY / prop.
 * Does NOT swallow errors / hide CRA overlays — Lynn rule: fix red errors, don't hide them.
 */
export const SafeTurnstile = forwardRef(function SafeTurnstile(
  {
    siteKey,
    onSuccess,
    onExpire,
    onError,
    onUnsupported,
    onUnavailable, // accepted for callers; marsidev has no onUnavailable — left intentional
    options,
    id: idProp,
    ...rest
  },
  ref
) {
  const reactId = useId().replace(/:/g, "");
  const widgetId = idProp || `cf-turnstile-${reactId}`;
  const { hostRef, ready } = useStableVisibleGate();

  const hostname =
    typeof window !== "undefined" ? window.location.hostname || "" : "";
  const previewHost = useMemo(() => isTurnstileBypassHost(hostname), [hostname]);
  const key = useMemo(() => {
    if (previewHost) return CF_TEST_VISIBLE_PASS;
    return siteKey || process.env.REACT_APP_TURNSTILE_SITE_KEY || "";
  }, [previewHost, siteKey]);

  if (!key) {
    // Missing key is a real config bug — surface it, don't silently no-op.
    throw new Error("REACT_APP_TURNSTILE_SITE_KEY is missing");
  }

  return (
    <div
      ref={hostRef}
      data-testid="safe-turnstile"
      className="space-y-2"
      data-turnstile-id={widgetId}
      data-turnstile-key={previewHost ? "cf-test" : "prod"}
    >
      {previewHost && (
        <p
          data-testid="turnstile-host-hint"
          className="text-[10px] uppercase tracking-wider text-[#00E5C0]/70"
        >
          Preview host <span className="text-white">{hostname}</span> — using
          Cloudflare test sitekey (always-pass) because the production key is not
          registered for this host.
        </p>
      )}
      {ready ? (
        <Suspense
          fallback={
            <div
              data-testid="turnstile-chunk-loading"
              className="min-h-[65px] w-full max-w-[300px]"
              aria-hidden
            />
          }
        >
          <Turnstile
            {...rest}
            ref={ref}
            id={widgetId}
            siteKey={key}
            onSuccess={onSuccess}
            onExpire={onExpire}
            onError={onError}
            onUnsupported={onUnsupported}
            options={options}
          />
        </Suspense>
      ) : (
        <div
          data-testid="turnstile-waiting-visible"
          className="min-h-[65px] w-full max-w-[300px]"
          aria-hidden
        />
      )}
    </div>
  );
});

export default SafeTurnstile;
